import React from 'react';
import PropTypes from 'prop-types';
import Loader from 'relements/build/esm/decorators/loader';
import connection from '@src/connection';

import { createAlert } from '@src/actions/BotActions';
import { BASE_URL } from '@src/const';
import { triggerDownload } from '@src/utils';
import { AlertTypes } from '@src/constants/MogamboConstants';
import cloneDeep from 'lodash/cloneDeep';
import styles from './Dictionary.scss';
import * as maps from './maps';

import DictionaryHeader from './DictionaryHeader';
import DictionaryTable from './DictionaryTable';

const DEFAULT_LANG = 'en';

@connection(maps.listenToStores, maps.getStateFromStores)
@Loader({})
export default class Dictionary extends React.Component {
  static propTypes = {
    new: PropTypes.bool,
    onChange: PropTypes.func,
    onAddLanguage: PropTypes.func,
    entityData: PropTypes.object,
    enabledLanguages: PropTypes.arrayOf(PropTypes.string),
    supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  };

  constructor(props) {
    super(props);

    this.state = {
      dictionary: {},
      savedDictionary: {},
      changedDictionary: {},
      deletedDictionary: {},
      saved: true,
      filters: {
        onlyEmptyVariants: false,
        searchTerm: '',
        page: 1,
        pageSize: 100,
      },
    };

    if (props.enabledLanguages && props.enabledLanguages[0]) {
      this.state.language = props.enabledLanguages[0];
    } else {
      this.state.language = DEFAULT_LANG;
    }

    this.validate = this.validate.bind(this);
    this._addLanguage = this._addLanguage.bind(this);
    this._handleUpload = this._handleUpload.bind(this);
    this._handleDownload = this._handleDownload.bind(this);
    this._handleAdd = this._handleAdd.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleDelete = this._handleDelete.bind(this);
    this._handleFilter = this._handleFilter.bind(this);
    this._saveDictionary = this._saveDictionary.bind(this);
  }

  componentDidMount = () => {
    this._search();
    this.props.customRef(this);
  };

  // WARNING! To be deprecated in React v17. Use new lifecycle static getDerivedStateFromProps instead.
  componentWillReceiveProps(nextProps) {
    if (!this.props.enabledLanguages[0] && nextProps.enabledLanguages[0]) {
      this.setState({ language: nextProps.enabledLanguages[0] });
    }
  }

  render() {
    const { filters, dictionary, saved } = this.state;
    const filtersDisabled = !saved || this.props.new;
    return (
      <div className={styles.dictionary}>
        <DictionaryHeader
          filters={filters}
          onFilter={this._handleFilter}
          onDownload={this._handleDownload}
          onUpload={!this.props.disabled ? this._handleUpload : null}
          filtersDisabled={filtersDisabled}
        />
        <DictionaryTable
          dictionary={Object.values(dictionary)}
          onAdd={this._handleAdd}
          onChange={this._handleChange}
          onDelete={this._handleDelete}
          onLanguageChange={(language) => this.setState({ language })}
          onAddLanguage={this._addLanguage}
          language={this.state.language}
          filters={filters}
          onFilter={this._handleFilter}
          supportedLanguages={this.props.supportedLanguages}
          enabledLanguages={this.props.enabledLanguages}
          editable={this.props.editable}
        />
      </div>
    );
  }

  setDictionary(dictionary, meta) {
    const { filters } = this.state;
    filters.numPages = Math.ceil(meta.total / filters.pageSize);
    filters.numWords = meta.total;
    const dictionaryObj = dictionary.reduce(
      (acc, val) => ({
        ...acc,
        [val.word]: { ...val, key: val.word },
      }),
      {}
    );
    this.setState({
      dictionary: dictionaryObj,
      savedDictionary: cloneDeep(dictionaryObj),
      filters,
    });
  }

  async _search() {
    this.props.activateLoader();

    const {
      searchTerm,
      onlyEmptyVariants,
      page,
      pageSize,
    } = this.state.filters;

    const searchResults = await maps.searchDictionary({
      dictionaryId: this.props?.entityData?.id || this.props?.entityId,
      dictionaryName: this.props?.entityData?.name || this.props?.entityName,
      searchTerm,
      onlyEmptyVariants,
      page: page - 1,
      pageSize,
    });

    this.setDictionary(searchResults.body, searchResults.meta);

    this.props.deactivateLoader();
  }

  async _addLanguage(newLanguage) {
    const data = {
      supported_languages: [...this.props.enabledLanguages, newLanguage],
    };

    if (!this.props.new) {
      this.props.activateLoader();
      const response = await maps.addLanguageToDictionary(
        this.props.entityName,
        this.props.entityId,
        data
      );
      this.props.deactivateLoader();
    }

    this.props.onAddLanguage(newLanguage);
    this.setState({ language: newLanguage });

    createAlert(
      `${newLanguage} added to dictionary`,
      undefined,
      AlertTypes.POSITIVE
    );
  }

  _handleChange({ key, type, value, language }) {
    const {
      dictionary,
      changedDictionary,
      deletedDictionary,
      savedDictionary,
    } = this.state;

    let dictionaryObj;
    let changedDictionaryObj;
    let deletedDictionaryObj;
    if (type === 'word') {
      const changedDictionaryItem = {
        ...dictionary[key],
        [type]: value,
      };
      dictionaryObj = {
        ...dictionary,
        [key]: changedDictionaryItem,
      };
      changedDictionaryObj = {
        ...changedDictionary,
        [key]: changedDictionaryItem,
      };
      deletedDictionaryObj = {
        ...deletedDictionary,
        [key]: savedDictionary[key],
      };
    } else if (type === 'variants') {
      const changedDictionaryItem = {
        ...dictionary[key],
        [type]: {
          ...dictionary[key][type],
          [language]: {
            ...dictionary[key][type][language],
            value,
          },
        },
      };
      dictionaryObj = {
        ...dictionary,
        [key]: changedDictionaryItem,
      };
      changedDictionaryObj = {
        ...changedDictionary,
        [key]: changedDictionaryItem,
      };

      deletedDictionaryObj = deletedDictionary;
    }

    this._saveDictionary(
      dictionaryObj,
      changedDictionaryObj,
      deletedDictionaryObj
    );
  }

  _handleDelete(key) {
    const { dictionary, changedDictionary, deletedDictionary } = this.state;

    const { [key]: deleted, ...restDictionary } = dictionary;

    const {
      [key]: deletedChanged,
      ...restInChangedDictionary
    } = changedDictionary;

    const deletedDictionaryObj = {
      ...deletedDictionary,
      [key]: deleted,
    };

    this._saveDictionary(
      restDictionary,
      restInChangedDictionary,
      deletedDictionaryObj
    );
  }

  _handleAdd(language) {
    const { dictionary, changedDictionary, deletedDictionary } = this.state;

    const key = `new_word_${new Date().valueOf()}`;
    const changedDictionaryItem = {
      key,
      word: '',
      variants: {
        [language]: {
          value: [],
        },
      },
    };
    const dictionaryObj = {
      [key]: changedDictionaryItem,
      ...dictionary,
    };

    const changedDictionaryObj = {
      ...changedDictionary,
      [key]: changedDictionaryItem,
    };

    this._saveDictionary(
      dictionaryObj,
      changedDictionaryObj,
      deletedDictionary
    );
  }

  _handleFilter(key, value) {
    const { filters } = this.state;
    filters[key] = value;
    if (key !== 'page') {
      filters.page = 1;
    }
    this.setState({ filters }, this._search);
  }

  _handleDownload() {
    const URL = `${BASE_URL}/entities/dictionary/csv/${this.props.entityData.name}`;
    triggerDownload('report.csv', URL);
  }

  async _handleUpload() {
    const fileUploadDOM = document.getElementById('upload-file-input');
    fileUploadDOM.value = null;
    fileUploadDOM.onchange = async (e) => {
      this.props.activateLoader();
      const file = e.target.files[0];
      await maps.uploadDictionary(
        this.props.entityData.name,
        this.props.entityData.id,
        file
      );
      setTimeout(() => this._search(), 500);
      createAlert(
        'Dictionary imported successfully',
        undefined,
        AlertTypes.POSITIVE
      );
    };

    fileUploadDOM.click();
  }

  _saveDictionary(dictionary, changedDictionary, deletedDictionary) {
    this.setState(
      {
        dictionary,
        changedDictionary,
        deletedDictionary,
        saved: false,
      },
      () => {
        this.props.onChange({
          edited: Object.values(changedDictionary).filter(
            (item) => !!item && item.word.trim()
          ),
          deleted: Object.values(deletedDictionary).filter((item) => !!item),
        });
      }
    );
  }

  validate(cb) {
    const { saved } = this.state;
    if (!saved) {
      maps.showConfirmationDialog({
        okTitle: 'Reset Dictionary ',
        cancelTitle: 'Cancel',
        title: 'Unsaved changes',
        message: 'You will lose all changes made to this dictionary',
        onOk: cb,
        onCancel: () => {},
        okActionType: 'danger',
      });
      return false;
    }

    return true;
  }

  reset() {
    this.setState({
      saved: true,
      savedDictionary: cloneDeep(this.state.dictionary),
    });
  }
}
